import { TabItem, Tabs } from '@globalmed/atlas-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './ProvisioningTab.module.css';
import { ContractPeriods } from '../ContractPeriods';
import { NumberOfSeatsTab } from '../NumberOfSeatsTab';
import {
  TCreateOrganizationForm,
  TOrganizationContractForm,
} from '../../schema';
import { PROVISIONING_TYPE_TABS } from '../../types';
import { useGlobalMedDisclaimer } from '../../utils/useGlobalMedDisclaimer';
import { EOrganizationProvisioningType } from '@/types';
import { createUnionGuard } from '@/utils/typeGuards';

const isProvisioningTypeTab = createUnionGuard(PROVISIONING_TYPE_TABS);

type TProps = {
  disabled?: boolean;
  isTypeLocked?: boolean;
  isScopeLocked?: boolean;
  occupiedSeats?: number;
  savedContracts?: TOrganizationContractForm[];
};

/**
 * Provisioning tab of the create organization modal.
 * @param props - Component props.
 * @param props.disabled - When true, all fields are locked for editing (Org
 * Admins) and show a disclaimer tooltip on hover; only the tab of the
 * organization's own provisioning type is shown.
 * @param props.isTypeLocked - When true, the provisioning type cannot be
 * switched: it is chosen once, when the organization is first saved.
 * @param props.isScopeLocked - When true, the provisioning scope cannot be
 * switched: it is chosen once, when the organization is first saved.
 * @param props.occupiedSeats - The organization's current occupied seats,
 * forwarded to the contract fields for their "cannot be lower than occupied"
 * error message.
 * @param props.savedContracts - The contracts as the organization was loaded,
 * forwarded to the contract periods to lock the ones that have started.
 * @returns JSX.Element representing the provisioning tab.
 */
export const ProvisioningTab = ({
  disabled = false,
  isTypeLocked = false,
  isScopeLocked = false,
  occupiedSeats,
  savedContracts,
}: TProps) => {
  const { watch, setValue } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();
  const withDisclaimer = useGlobalMedDisclaimer(disabled);

  const provisioningType = watch('provisioning.type');

  const isTypeSwitchDisabled = disabled || isTypeLocked;

  const items: TabItem[] = [
    {
      key: EOrganizationProvisioningType.SEATS,
      title: t('create-organization-modal.number-of-seats'),
      alert: false,
    },
    {
      key: EOrganizationProvisioningType.CONSULTS_PER_MONTH,
      title: t('create-organization-modal.consults-per-month'),
      alert: false,
    },
  ].filter((item) => !disabled || item.key === provisioningType);

  return (
    <div className={styles.flexColumn}>
      {withDisclaimer(
        <Tabs
          items={items}
          variant="button"
          size="sm"
          activeKey={provisioningType}
          disabled={isTypeSwitchDisabled}
          onChange={(item) => {
            if (isTypeSwitchDisabled) return;
            if (!isProvisioningTypeTab(item.key)) return;
            setValue('provisioning.type', item.key, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
        />,
      )}

      {provisioningType === EOrganizationProvisioningType.SEATS && (
        <NumberOfSeatsTab disabled={disabled} isScopeLocked={isScopeLocked} />
      )}

      <ContractPeriods
        disabled={disabled}
        occupiedSeats={occupiedSeats}
        savedContracts={savedContracts}
      />
    </div>
  );
};
